import { useGetHeliosFilesListQuery, useUploadHeliosFilesMutation } from 'store/services/helios/files';
import type { ChangeEvent } from 'react';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import type { FieldValues, Path, UseFormSetValue } from 'react-hook-form';

import { Loader } from 'components/common';
import { getBaseUrl } from 'utils/baseUrl';
import { Button } from 'components/mui/common/Button';
import { ActionModal } from 'components/mui/common/ActionModal';
import { FileItem } from 'components/user/DummyHelios/Uploader/FileItem';

import { FileItemSkeleton } from 'components/user/Records/components/FileUploadsItem/FileItemSkeleton';
import type { FileListEvent } from 'components/user/Records/components/FileUploadsItem';
import { FilesWrapper, InputWrapper, RootWrapper } from 'components/user/Records/components/FileUploadsItem/FileUploadsItem.styles';
import type { ModalsType } from 'types';
import type { UploadHeliosFilesReq, UploadHeliosFilesResponse } from 'types/helios/files';
import type { HeliosCellRawValue } from 'types/helios/agendaListing';
import { getFileUploadError, MAX_FILES_PER_UPLOAD, MAX_UPLOAD_FILE_SIZE_MB, splitFileNameAndExtension } from 'utils/file/fileUtils';

interface Props<K extends FieldValues> extends ModalsType {
  setValue: UseFormSetValue<K>;
  fieldName: Path<K>;
  dynamicRelationId?: number | null;
  templateItemId?: number;
  templateViewId?: string;
  recordNumber?: number | null;
  value?: HeliosCellRawValue | null;
  isNew: boolean;
  changeValue?: (value: number) => void;
}

export const FileEditModal = memo(
  <K extends FieldValues>({
    open,
    onClose,
    setValue,
    fieldName,
    dynamicRelationId,
    templateItemId,
    templateViewId,
    recordNumber,
    value,
    isNew,
    changeValue,
  }: Props<K>) => {
    const { t } = useTranslation();
    const selectedRows = useRef<string[]>([]);
    const [uploadedFilesData, setUploadedFilesData] = useState<UploadHeliosFilesResponse[]>([]);
    const [addedFiles, setAddedFiles] = useState<number[]>([]);
    const [removedFiles, setRemovedFiles] = useState<number[]>([]);
    const [uploadError, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const uploadInProgress = useRef(false);
    const [isLoading, setIsLoading] = useState(false);

    const isSingleFile = !dynamicRelationId;
    const maxFilesPerUpload = dynamicRelationId ? MAX_FILES_PER_UPLOAD : 1;

    const [uploadFiles] = useUploadHeliosFilesMutation();

    const { data: filesList, isFetching } = useGetHeliosFilesListQuery(
      {
        templateId: templateViewId,
        templateItemId,
        templateItemValue: value as string,
        recordId: recordNumber?.toString() ?? '',
      },
      {
        skip: !open || isSingleFile || isNew,
      }
    );

    const singleFile = useMemo(() => {
      if (isSingleFile && typeof value === 'object' && value !== null && value.id) {
        return {
          fileId: value.id ?? 0,
          name: value.name ?? '',
          url: getBaseUrl(`/api/helios/Files/DownloadFile?templateId=${templateViewId}&fileId=${value.id}`),
        };
      }
    }, [templateViewId, value, isSingleFile]);

    const isDisable = useMemo(() => {
      if (isLoading) return true;
      if (!isSingleFile) return false;
      return addedFiles.length > 0 && (!singleFile || (singleFile && removedFiles.includes(singleFile.fileId)));
    }, [isLoading, isSingleFile, singleFile, removedFiles, addedFiles]);

    const handleRemoveFile = useCallback(
      (fileId: number) => {
        if (addedFiles.includes(fileId)) {
          setAddedFiles((prev) => prev.filter((id) => id !== fileId));
        } else {
          setRemovedFiles((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
        }
        if (uploadedFilesData.some((file) => file.fileId === fileId)) {
          setUploadedFilesData((prev) => prev.filter((file) => file.fileId !== fileId));
        } else {
          setRemovedFiles((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
        }
      },
      [addedFiles, uploadedFilesData]
    );

    const handleAddFile = useCallback((filesData: UploadHeliosFilesResponse[]) => {
      const ids = filesData.map((file) => file.fileId);
      setAddedFiles((prev) => [...prev, ...ids]);
      setUploadedFilesData((prev) => [...prev, ...filesData]);
    }, []);

    const fileContent = useCallback(() => {
      if (isSingleFile && singleFile) {
        if (removedFiles.includes(singleFile.fileId)) return null;
        return <FileItem onRemove={() => handleRemoveFile(singleFile.fileId)} file={singleFile} />;
      }
      if (isFetching) return <Loader />;

      return (
        <>
          {filesList &&
            filesList
              .filter((file) => !removedFiles.includes(file.id))
              .map((file) => (
                <FileItem
                  key={file.id}
                  onRemove={() => handleRemoveFile(file.id)}
                  file={{
                    fileId: file?.id,
                    name: file?.name,
                    extension: file?.extension,
                    url: getBaseUrl(`/api/helios/Files/DownloadFile?templateId=${templateViewId}&fileId=${file.id}`),
                  }}
                />
              ))}

          {uploadedFilesData.map((file) => {
            const { name, extension } = splitFileNameAndExtension(file.nameWithExtension);
            return (
              <FileItem
                key={file.fileId}
                onRemove={() => handleRemoveFile(file.fileId)}
                file={{
                  fileId: file.fileId,
                  name,
                  extension,
                  url: getBaseUrl(`/api/helios/Files/DownloadFile?templateId=${templateViewId}&fileId=${file.fileId}`),
                }}
              />
            );
          })}
        </>
      );
    }, [isSingleFile, singleFile, isFetching, filesList, uploadedFilesData, removedFiles, handleRemoveFile, templateViewId]);

    const handleClose = useCallback(() => {
      selectedRows.current = [];
      onClose();
    }, [onClose]);

    const handleSubmit = useCallback(() => {
      if (uploadInProgress.current) {
        return setError(t('user.detail.file.uploadInProgress'));
      }
      if (isSingleFile) {
        setValue(fieldName, addedFiles[0] as unknown as K[typeof fieldName], { shouldDirty: true });
        if (addedFiles.length > 0) changeValue?.(1);
      } else {
        setValue(
          fieldName,
          {
            add: addedFiles,
            remove: removedFiles,
          } as unknown as K[typeof fieldName],
          { shouldDirty: true }
        );
        const currentNumberOfFiles = filesList?.length ?? 0;
        changeValue?.(currentNumberOfFiles + addedFiles.length - removedFiles.length);
      }
      handleClose();
    }, [isSingleFile, handleClose, setValue, fieldName, addedFiles, changeValue, removedFiles, filesList?.length, t]);

    const handleButtonOpenUpload = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!inputRef || !inputRef.current || isDisable) return;

        inputRef.current.click();
      },
      [isDisable, inputRef]
    );

    const handleUploadFile = useCallback(
      async (event: ChangeEvent<HTMLInputElement> | FileListEvent | File[]) => {
        const files = Array.isArray(event) ? event : Array.from(event.target.files || []);
        if (!Array.isArray(event)) event.target.value = '';
        if (files.length === 0) return;

        const validationError = getFileUploadError(files, maxFilesPerUpload, uploadInProgress.current);
        if (validationError) {
          return setError(t(validationError, { max: MAX_UPLOAD_FILE_SIZE_MB, maxFiles: maxFilesPerUpload }));
        }

        uploadInProgress.current = true;
        setIsLoading(true);
        setError(null);
        try {
          const createPayload = async (): Promise<UploadHeliosFilesReq> => {
            const objects = await Promise.all(
              files.map(
                (file) =>
                  new Promise<UploadHeliosFilesReq['files'][number]>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = () => {
                      const base64Data = (reader.result as string).split(',')[1];
                      const { name, extension } = splitFileNameAndExtension(file.name);
                      resolve({
                        fileName: name,
                        data: base64Data,
                        extension: extension.slice(1),
                      });
                    };
                    reader.onerror = (error) => reject(error);
                  })
              )
            );

            const payload: UploadHeliosFilesReq = {
              templateId: templateViewId ?? '',
              templateItemId: templateItemId ?? 0,
              files: objects,
            };

            return payload;
          };

          const payload = await createPayload();
          const res = await uploadFiles({ payload });

          if (!('error' in res)) {
            toast.success(t('common.uploadFileSuccess'));
            handleAddFile(res?.data);
          } else {
            toast.error(t('common.uploadFileError'));
          }
        } catch {
          toast.error(t('common.uploadFileError'));
        } finally {
          uploadInProgress.current = false;
          setIsLoading(false);
        }
      },
      [templateViewId, handleAddFile, maxFilesPerUpload, t, templateItemId, uploadFiles]
    );

    const onDrop = useCallback((data: File[]) => handleUploadFile(data), [handleUploadFile]);

    const { getRootProps, getInputProps } = useDropzone({
      onDrop,
      noClick: true,
      disabled: isDisable,
    });

    return (
      <ActionModal maxWidth="md" open={open} onClose={handleClose} onCancel={handleClose} onSubmit={handleSubmit} title={t('common.files')}>
        <>
          <RootWrapper {...getRootProps()}>
            {isDisable ? null : (
              <Button variant="outlined" type="button" sx={{ width: '128px' }} onClick={handleButtonOpenUpload}>
                + {t('common.upload')}
              </Button>
            )}

            <InputWrapper>
              <input
                {...getInputProps()}
                ref={inputRef}
                disabled={isDisable}
                type="file"
                multiple={maxFilesPerUpload > 1}
                style={{ display: 'none' }}
                onChange={handleUploadFile}
              />
              <FilesWrapper>
                {fileContent()}
                {isLoading && <FileItemSkeleton />}
              </FilesWrapper>
            </InputWrapper>
          </RootWrapper>
          {uploadError && (
            <Typography variant="body1" color="error">
              {uploadError}
            </Typography>
          )}
        </>
      </ActionModal>
    );
  }
) as <K extends FieldValues>(props: Props<K>) => JSX.Element;
